import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Euro, BedDouble, Bath, Maximize, MapPin, Phone, Mail, Calendar, Award, Trees, Zap, Building2, Share2, Printer, ArrowLeft, ThumbsUp, ChevronRight, Shield, ParkingSquare, Home, Wine, Wind, Thermometer, Map, FileText } from 'lucide-react';
import { useProperty } from '../hooks/useProperty';
import { useProperties } from '../hooks/useProperties';
import { useContactForm } from '../hooks/useContactForm';
import { motion } from 'framer-motion';
import PropertyCard from '../components/property/PropertyCard';
import { toast } from 'react-toastify';
import generatePropertyPDF from '../utils/generatePropertyPDF';
import { getPropertyPriceLabel, getPropertyPriceDetail, getPublicPriceFields } from '../utils/propertyPrice';
import { getHeatingLabel } from '../utils/heating';
import { isLandPropertyType } from '../utils/propertyApplicability';
import PropertySharingModal from './PropertyDetail/components/PropertySharingModal';
import PropertyFloorplan from './PropertyDetail/components/PropertyFloorplan';
import PropertyGallery from '../components/property/PropertyGallery';
import { PLACEHOLDER_IMAGE } from '../utils/imageProxy';

const getPropertyImages = (property: any) => {
  if (!property?.property_images?.length) {
    return [{
      original: property.primaryImage || PLACEHOLDER_IMAGE,
      thumbnail: property.primaryImage || PLACEHOLDER_IMAGE,
      thumbnailLoading: 'lazy',
      originalAlt: 'Immagine non disponibile',
      thumbnailAlt: 'Thumbnail non disponibile'
    }];
  }

  // Sort the images: primary first, then by position
  const sortedImages = [...property.property_images].sort((a, b) => {
    // Put primary image first
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    
    // Then sort by position if available
    if (a.position !== undefined && b.position !== undefined) {
      return a.position - b.position;
    }
    
    // Fallback to creation date
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return sortedImages.map((img: any) => ({
    original: img.url,
    thumbnail: img.url,
    thumbnailLoading: 'lazy',
    originalAlt: property.title || 'Immagine immobile',
    thumbnailAlt: `Thumbnail di ${property.title || 'immobile'}`
  }));
};

// Helper to get appropriate icon for a feature
const getFeatureIcon = (featureName: string) => {
  // Map feature names to appropriate icons
  const iconMap: {[key: string]: React.ElementType} = {
    // Esterni
    'Parcheggio': ParkingSquare,
    'Giardino': Trees,
    'Terrazzo': Home,
    'Balcone': Home,
    'Garage': ParkingSquare,
    'Box Auto': ParkingSquare,
    'Piscina': Bath,
    
    // Interni
    'Aria Condizionata': Wind,
    'Riscaldamento Autonomo': Thermometer,
    
    // Edificio
    'Ascensore': Home,
    'Portiere': Shield,
    'Arredato': Home,
    
    // Caratteristiche
    'Vista Panoramica': Home,
    'Cantina': Wine,
    'Mansarda': Home,
    'Taverna': Home
  };
  
  return iconMap[featureName] || Award;
};

function PropertyDetail() {
  const { id } = useParams();
  const { property, loading, error } = useProperty(id || '');
  const { submitContactForm, loading: contactLoading } = useContactForm();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isFloorplanModalOpen, setIsFloorplanModalOpen] = useState(false);

  // Get similar properties
  const { properties } = useProperties({
    type: property?.type,
    location: property?.location?.split(',')[1]?.trim(), // Get city from location
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Prepara i dati del messaggio in formato leggibile
      const formattedMessage = `
Sono interessato all'immobile:
${property.title}
Riferimento: ${property.reference_number || `MOL${1000 + parseInt(property.id.slice(-3), 16)}`}

${contactForm.message}
      `;

      const { success, error } = await submitContactForm({
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: formattedMessage,
        property_id: property.id,
        type: 'property'
      }, {
        title: property.title,
        ref: property.reference_number || `RIF-${property.id.substring(0, 8)}`,
        url: window.location.href
      });

      if (success) {
        toast.success("La tua richiesta è stata inviata con successo!");
        setContactForm({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        toast.error(`Errore nell'invio della richiesta: ${error}`);
      }
    } catch (error) {
      toast.error("Si è verificato un errore. Riprova più tardi.");
    }
  };

  const handleGeneratePDF = () => {
    try {
      // Genera il PDF e aprilo in una nuova scheda
      generatePropertyPDF(property, true);
      toast.success('PDF generato con successo');
    } catch (error) {
      console.error('Errore nella generazione del PDF:', error);
      toast.error('Si è verificato un errore nella generazione del PDF. Riprova più tardi.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento immobile...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Immobile non trovato</h2>
          <p className="text-gray-600">{error || "L'immobile che stai cercando non esiste."}</p>
        </div>
      </div>
    );
  }

  const isLand = isLandPropertyType(property.property_types || { id: property.type_id });
  const showBathrooms = !isLand || property.bathrooms > 0;
  const showState = !isLand || Boolean(property.property_states?.name?.trim());
  const descriptionFacts = [
    `${property.bedrooms} locali`,
    ...(showBathrooms ? [`${property.bathrooms} bagni`] : []),
    `${property.size} m²`,
  ].join(', ');
  const metaDescription = `${property.title} - ${property.location}. ${descriptionFacts}. ${property.type === 'vendita' ? 'In vendita' : 'In affitto'} a ${property.zones?.name || 'Scandicci'}.`;

  return (
    <div className="bg-gray-50">
      <Helmet>
        <title>{property.title} | Molinaro Immobiliare</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${property.title}, ${property.location}, immobile, ${property.type === 'vendita' ? 'vendita' : 'affitto'}, ${property.zones?.name || 'Scandicci'}, ${property.bedrooms} locali, ${property.size} m²`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.molinaroimmobiliare.it/immobile/${property.id}`} />
        <meta property="og:title" content={`${property.title} | Molinaro Immobiliare`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={property.primaryImage || property.property_images?.[0]?.url || 'https://www.molinaroimmobiliare.it/images/agenzia1.jpeg'} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://www.molinaroimmobiliare.it/immobile/${property.id}`} />
        <meta property="twitter:title" content={`${property.title} | Molinaro Immobiliare`} />
        <meta property="twitter:description" content={metaDescription} />
        <meta property="twitter:image" content={property.primaryImage || property.property_images?.[0]?.url || 'https://www.molinaroimmobiliare.it/images/agenzia1.jpeg'} />
        
        {/* Structured Data for Real Estate Property */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateListing",
            "name": property.title,
            "description": property.description?.replace(/<[^>]*>?/gm, '') || `${property.title} - ${property.location}`,
            "url": `https://www.molinaroimmobiliare.it/immobile/${property.id}`,
            "image": property.primaryImage || property.property_images?.[0]?.url,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": property.zones?.name || "Scandicci",
              "addressRegion": "FI",
              "addressCountry": "IT",
              "streetAddress": property.location
            },
            "offers": {
              "@type": "Offer",
              ...getPublicPriceFields(property),
              "availability": "https://schema.org/InStock"
            },
            "numberOfRooms": property.total_rooms || property.bedrooms,
            ...(showBathrooms ? { "numberOfBathroomsTotal": property.bathrooms } : {}),
            "floorSize": {
              "@type": "QuantitativeValue",
              "value": property.size,
              "unitCode": "MTK"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between gap-5 text-white">
            <div>
              <Link to="/immobili" className="inline-flex items-center text-gray-200 hover:text-white mb-3">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Torna agli immobili
              </Link>
              <h1 className="text-3xl md:text-4xl font-monomakh mb-2">{property.title}</h1>
              <div className="flex items-center mb-1">
                <Building2 className="h-4 w-4 mr-1 text-secondary" />
                <span className="text-gray-200">{property.zones?.name}</span>
              </div>
              <div className="flex items-center">
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Rif. {property.reference_number || `MOL${1000 + parseInt(property.id.slice(-3), 16)}`}</span>
                <span className="mx-2">•</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  Pubblicato il {new Date().toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'})}
                </span>
              </div>
            </div>
            <div className="flex flex-col justify-center items-end">
              <div className="text-3xl font-bold mb-1">{getPropertyPriceLabel(property)}</div>
              {getPropertyPriceDetail(property) && (
                <div className="text-gray-200 text-sm">{getPropertyPriceDetail(property)}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Property Images */}
              <div className="relative">
                <PropertyGallery 
                  property={property}
                  onShareClick={() => setIsShareModalOpen(true)}
                  onPrintClick={handleGeneratePDF}
                />
              </div>

              {/* Quick Info Bar */}
              <div className="bg-gray-50 p-4 border-y border-gray-200">
                <div className={`grid ${showBathrooms ? 'grid-cols-4' : 'grid-cols-3'} divide-x divide-gray-300`}>
                  <div className="flex flex-col items-center justify-center text-center px-2">
                    <BedDouble className="h-5 w-5 text-primary mb-1" />
                    <div className="text-sm font-medium">{property.bedrooms} {property.bedrooms === 1 ? 'Camera' : 'Camere'}</div>
                  </div>
                  {showBathrooms && <div className="flex flex-col items-center justify-center text-center px-2">
                    <Bath className="h-5 w-5 text-primary mb-1" />
                    <div className="text-sm font-medium">{property.bathrooms} {property.bathrooms === 1 ? 'Bagno' : 'Bagni'}</div>
                  </div>}
                  <div className="flex flex-col items-center justify-center text-center px-2">
                    <Maximize className="h-5 w-5 text-primary mb-1" />
                    <div className="text-sm font-medium">{property.size} m²</div>
                  </div>
                  <div className="flex flex-col items-center justify-center text-center px-2">
                    <Zap className="h-5 w-5 text-primary mb-1" />
                    <div className="text-sm font-medium">Classe {property.energy_class || 'N/D'}</div>
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-primary mb-4">Descrizione</h2>
                <div className="prose max-w-none mb-8">
                  <div dangerouslySetInnerHTML={{ __html: property.description || '' }} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h3 className="text-xl font-monomakh text-primary mb-4 flex items-center">
                      <ThumbsUp className="h-5 w-5 mr-2 text-secondary" />
                      Punti di Forza
                    </h3>
                    <ul className="space-y-2">
                      {property.property_strengths?.map((strength: any, index: number) => (
                        <li key={index} className="flex items-start">
                          <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{strength.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-monomakh text-primary mb-4">Dotazioni</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {property.property_features?.map((pf: any, index: number) => {
                        const FeatureIcon = pf.features?.name 
                          ? getFeatureIcon(pf.features.name) 
                          : Award;
                        
                        return (
                          <div key={index} className="bg-gray-50 p-3 rounded flex items-center">
                            <FeatureIcon className="h-4 w-4 text-primary mr-2" />
                            <span className="text-sm">{pf.features?.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary/5 p-5 rounded-lg mb-8">
                  <h3 className="text-xl font-monomakh text-primary mb-3">Dettagli Immobile</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Tipologia</span>
                      <span className="font-medium text-sm">{property.property_types?.name || 'N/D'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Superficie</span>
                      <span className="font-medium text-sm">{property.size} m²</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Locali</span>
                      <span className="font-medium text-sm">{property.total_rooms || property.bedrooms || 'N/D'}</span>
                    </div>
                    {showBathrooms && <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Bagni</span>
                      <span className="font-medium text-sm">{property.bathrooms}</span>
                    </div>}
                    {showState && <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Stato</span>
                      <span className="font-medium text-sm">{property.property_states?.name || 'N/D'}</span>
                    </div>}
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Riscaldamento</span>
                      <span className="font-medium text-sm">{getHeatingLabel(property) || 'N/D'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Classe energetica</span>
                      <span className="font-medium text-sm">{property.energy_class || 'N/D'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="text-gray-600 text-sm">Spese cond.</span>
                      <span className="font-medium text-sm">{property.extra_costs || 'N/D'}</span>
                    </div>
                  </div>
                </div>

                {/* Sezione Planimetria */}
                {property.planimetria && (
                  <div className="mt-8 p-6 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl shadow-md border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-bl-full -z-10"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/10 rounded-tr-full -z-10"></div>
                    
                    <h3 className="text-xl font-monomakh text-primary mb-5 flex items-center">
                      <Map className="h-6 w-6 mr-2 text-secondary" />
                      Planimetria dell'Immobile
                    </h3>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                      <p className="text-gray-700 mb-4 sm:mb-0 max-w-2xl">
                        Visualizza la planimetria dettagliata dell'immobile per avere una migliore comprensione 
                        degli spazi e della disposizione delle stanze.
                      </p>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsFloorplanModalOpen(true)}
                        className="bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-lg transition-colors flex items-center shadow-lg group"
                      >
                        <FileText className="h-5 w-5 mr-2 group-hover:animate-pulse" />
                        <span className="font-medium">Visualizza Planimetria</span>
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h3 className="text-xl font-semibold text-primary mb-4">Richiedi Informazioni</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome e Cognome
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Messaggio
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                    placeholder="Sono interessato a questo immobile..."
                  ></textarea>
                </div>
                <div className="flex items-start">
                  <input 
                    type="checkbox" 
                    id="privacy" 
                    required
                    className="h-4 w-4 text-primary border-gray-300 rounded mt-1" 
                  />
                  <label htmlFor="privacy" className="ml-2 block text-xs text-gray-500">
                    Autorizzo il trattamento dei miei dati personali ai sensi del GDPR (UE 2016/679)
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={contactLoading}
                  className="w-full bg-primary hover:bg-primary-light text-white font-semibold px-4 py-2 rounded-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {contactLoading ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Invio in corso...
                    </>
                  ) : (
                    "Invia Richiesta"
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex items-center text-gray-600">
                  <Phone className="h-5 w-5 mr-3 text-primary" />
                  <span>+39 327 8855115</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Mail className="h-5 w-5 mr-3 text-primary" />
                  <span>info@molinaroimmobiliare.it</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Calendar className="h-5 w-5 mr-3 text-primary" />
                  <span>Lun - Ven: 9:30 - 12:30 | 15:30 - 19:30</span>
                </div>
              </div>
              
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-primary mb-2">Hai domande?</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Saremo lieti di fornirti tutte le informazioni di cui hai bisogno su questo immobile
                </p>
                <a 
                  href="tel:+393278855115" 
                  className="block w-full bg-secondary hover:bg-secondary-light text-white px-4 py-2 rounded-md transition-colors font-semibold text-center"
                >
                  Chiama Ora
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Properties */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-monomakh text-primary mb-8">Immobili Simili</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {properties
              .filter(p => p.id !== property.id)
              .slice(0, 3)
              .map((relatedProperty, index) => (
                <PropertyCard 
                  key={relatedProperty.id}
                  property={relatedProperty}
                  index={index}
                />
              ))}
          </div>
        </div>
      </section>

      {/* Sharing Modal */}
      <PropertySharingModal 
        property={property}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      {/* Planimetria Modal */}
      <PropertyFloorplan 
        planimetriaUrl={property.planimetria}
        propertyName={property.title}
        isOpen={isFloorplanModalOpen}
        onClose={() => setIsFloorplanModalOpen(false)}
      />
    </div>
  );
}

export default PropertyDetail;
